import { useState } from 'react';
import { RichTextElement } from '@kontent-ai/gatsby-components';
import { graphql } from 'gatsby';
import { type BlockHTMLId } from '@/utils/renderBlock';
import {
  Container,
  Content,
  Headline,
  Section,
  IconContainer,
  InformationBlock,
  StyledIcon,
  StyledWrapper,
  SuccessContainer,
  SuccessHeadline,
  SuccessIcon,
  FormContainer,
} from './MidPageFormContainer.styles';
import NewsletterForm from '../Forms/NewsletterForm/NewsletterForm';

/**
 * This interface allows us to use a children prop,
 * so that we can use composition in storybook and unit tests to interact
 * with setStatus. See MidPageFormContainerProps's stories or tests for usage
 */
interface MidPageFormContainerProps extends Queries.MID_PAGE_FORM_CONTAINERFragment, BlockHTMLId {
  children?: (setStatus: (status: 'default' | 'success') => void) => JSX.Element;
}

const MidPageFormContainer = ({
  midPageFormContainerElements: elements,
  system,
  id,
  children,
}: MidPageFormContainerProps) => {
  const [status, setStatus] = useState<'default' | 'success'>('default');

  const { headline, success_headline, body, success_body, icon, success_icon, options, form } =
    elements;
  const successHeadlineValue = success_headline.value;
  const successBodyValue = success_body.value;
  const headlineValue = headline.value;
  const bodyValue = body.value;
  const hasDropShadow = options?.value?.includes('form_drop_shadow') ?? false;
  return (
    <Section data-codename={system.codename} id={id}>
      <StyledWrapper>
        {status === 'success' ? (
          <SuccessContainer>
            <div>
              <SuccessHeadline>{successHeadlineValue}</SuccessHeadline>
              <Content>
                <RichTextElement value={successBodyValue} />
                {children ? children(setStatus) : null}
              </Content>
            </div>
            {success_icon?.value && (
              <IconContainer>
                <SuccessIcon {...success_icon.value} />
              </IconContainer>
            )}
          </SuccessContainer>
        ) : (
          <Container>
            <InformationBlock>
              {icon?.value && (
                <IconContainer>
                  <StyledIcon {...icon.value} />
                </IconContainer>
              )}
              <Headline>{headlineValue}</Headline>
              <Content>
                <RichTextElement value={bodyValue} />
              </Content>
            </InformationBlock>
            <FormContainer hasDropShadow={hasDropShadow}>
              <NewsletterForm {...form.value} onSubmitSuccess={() => setStatus('success')} />
              {children ? children(setStatus) : null}
            </FormContainer>
          </Container>
        )}
      </StyledWrapper>
    </Section>
  );
};

export const fragment = graphql`
  fragment MID_PAGE_FORM_CONTAINER on kontent_item_midpage_form_container {
    __typename
    system {
      codename
    }
    midPageFormContainerElements: elements {
      ...COMPONENT_A_B_TEST
      ...COMPONENT_PERSONALIZATION
      headline {
        value
      }
      body {
        value
      }
      icon {
        value {
          url
        }
      }
      success_headline {
        value
      }
      success_body {
        value
      }
      success_icon {
        value {
          url
        }
      }
      options {
        value
      }
      form {
        value {
          ...NEWSLETTER_FORM
        }
      }
    }
  }
`;

export default MidPageFormContainer;
